<script setup>
import { ref, onMounted, computed } from 'vue';
import { useAssistenciaTecnicaStore } from '@/stores/assistenciaTecnica';
import { useNotificationStore } from '@/stores/notification';
import EditarAssistenciaTecnicaModal from '@/components/modals/EditarAssistenciaTecnicaModal.vue';
import useConfirmation from '@/composables/useConfirmation';
import RejeitarAssistenciaModal from '@/components/assistencia/RejeitarAssistenciaModal.vue';
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue';

const props = defineProps({
  assistencia: {
    type: Object,
    required: true
  }
});

const assistenciaStore = useAssistenciaTecnicaStore();
const notificationStore = useNotificationStore();

// Estado loading para o botão "Estou na porta"
const loadNotificarEstouNaPorta = ref(false);

// Estado loading para o botão "Avisar assistência"
const avisandoAssistencia = ref(false);

// Modal de edição
const editModalOpen = ref(false);
const editModalLoading = ref(false);

// Modal de observação
const observationModalOpen = ref(false);
const observationData = ref({
  assistencia: null,
  text: '',
  type: '' // 'assistencia' or 'revenda'
});

// Modal de rejeição
const rejectionModalOpen = ref(false);
const rejectionLoading = ref(false);

// State tracking para operações
const operationsLoading = ref({});

// State for status dropdown
const selectedStatus = ref(null);

// Track when an operation is being performed
const isOperationLoading = (assistenciaId, operationId) => {
  const key = `${assistenciaId}-${operationId}`;
  return !!operationsLoading.value[key];
};

// Helper para setting loading state
const setOperationLoading = (assistenciaId, operationId, loading) => {
  const key = `${assistenciaId}-${operationId}`;
  if (loading) {
    operationsLoading.value[key] = true;
  } else {
    delete operationsLoading.value[key];
  }
};

// Handle status selection
const onStatusChange = async (statusId) => {
  if (!statusId) return;
  
  // Special handling for rejection (status 4)
  if (statusId === '4') {
    rejectionModalOpen.value = true;
    selectedStatus.value = null;
    return;
  }
  
  // Define status labels based on your business requirements
  const statusLabels = {
    '2': 'aceitar',
    '4': 'rejeitar',
    '8': 'marcar como saiu para assistência',
    '3': 'marcar como concluída',
  };
  
  try {
    const action = statusLabels[statusId] || 'alterar o status de';
    
    await confirm({
      title: 'Alterar Status',
      message: `Tem certeza que deseja ${action} esta assistência técnica?`,
      confirmColor: 'primary',
      confirmText: 'Sim, Alterar'
    });
    
    setLoading(true);
    
    const success = await handleAlterarStatus(props.assistencia, statusId);
    
    if (!success) {
      throw new Error('Failed to update status');
    }
  } catch (error) {
    if (error !== 'User cancelled') {
      console.error('Erro ao atualizar status:', error);
    }
  } finally {
    setLoading(false);
    selectedStatus.value = null;
  }
};

// Alterar status da assistência
const handleAlterarStatus = async (assistencia, statusId) => {
  if (isOperationLoading(assistencia.id, `status-${statusId}`)) {
    return false;
  }
  
  try {
    setOperationLoading(assistencia.id, `status-${statusId}`, true);
    
    let result;
    
    // Prepare the payload with idrevenda
    const payload = {
      idrevenda: assistencia.revenda.id
    };
    
    // Chamar a API de acordo com o status selecionado
    switch (statusId) {
      case '2': // Aceitar assistência
        result = await assistenciaStore.aceitarAssistencia(assistencia.id, payload);
        break;
      case '3': // Assistência entregue
        result = await assistenciaStore.assistenciaEntregue(assistencia.id, payload);
        break;
      case '8': // Saiu para assistência
        result = await assistenciaStore.sairAssistencia(assistencia.id, payload);
        break;
      default:
        throw new Error(`Status não suportado: ${statusId}`);
    }
    
    if (result.success) {
      showNotification('Status da assistência atualizado com sucesso!', 'success');
      return true;
    } else {
      showNotification(result.message || 'Falha ao alterar status', 'error');
      return false;
    }
  } catch (error) {
    console.error('Erro ao alterar status da assistência:', error);
    showNotification('Erro ao alterar status da assistência', 'error');
    return false;
  } finally {
    setOperationLoading(assistencia.id, `status-${statusId}`, false);
  }
};

// Função para notificar "Estou na porta"
const handleNotificarEstouNaPorta = async (assistencia) => {
  if (!assistencia?.id) return;

  loadNotificarEstouNaPorta.value = true;
  
  try {
    const response = await assistenciaStore.notificarEstouNaPorta(assistencia.id);
    // Mostrar notificação de sucesso ou erro
    showNotification(response.message, response.success ? 'success' : 'error');
  } catch (error) {
    console.error('Erro ao notificar "Estou na porta":', error);
    showNotification('Erro ao notificar "Estou na porta"', 'error');
  } finally {
    loadNotificarEstouNaPorta.value = false;
  }
};

// Função para avisar assistência
const handleAvisarAssistencia = async (assistencia) => {
  if (avisandoAssistencia.value) return;

  avisandoAssistencia.value = true;
  
  try {
    // Chamar a API para avisar assistência
    const response = await assistenciaStore.avisarAssistencia(assistencia.id, assistencia.revenda.revendedor.id);
    
    // Mostrar notificação de sucesso ou erro
    showNotification(response.message, response.success ? 'success' : 'error');
  } catch (error) {
    console.error('Erro ao avisar assistência:', error);
    showNotification('Erro ao avisar assistência', 'error');
  } finally {
    avisandoAssistencia.value = false;
  }
};

// Função para abrir o modal de edição
const openEditModal = () => {
  editModalOpen.value = true;
};

// Função para salvar a assistência editada
const saveEditedAssistencia = async (formData) => {
  if (!props.assistencia?.id) return;
  
  editModalLoading.value = true;
  
  try {
    const response = await assistenciaStore.editarAssistencia(props.assistencia.id, formData);
    
    if (response.success) {
      showNotification('Assistência técnica atualizada com sucesso!', 'success');
      editModalOpen.value = false;
    } else {
      showNotification('Erro ao atualizar assistência técnica', 'error');
    }
  } catch (error) {
    console.error('Erro ao editar assistência técnica:', error);
    showNotification('Erro ao atualizar assistência técnica', 'error');
  } finally {
    editModalLoading.value = false;
  }
};

// Função para fechar o modal de edição
const cancelEditModal = () => {
  editModalOpen.value = false;
};

// Função para mostrar notificações usando o store
const showNotification = (message, type = 'success') => {
  notificationStore.showNotification(message, type);
};

// Função para copiar para a área de transferência
const copyToClipboard = (text, label = 'Texto') => {
  navigator.clipboard.writeText(text).then(() => {
    showNotification(`${label} copiado!`, 'success');
  }).catch(err => {
    console.error('Erro ao copiar para o clipboard:', err);
    showNotification('Erro ao copiar para a área de transferência', 'error');
  });
};

// Adicionar a função que estava faltando para notificação via WhatsApp
const openWhatsApp = (telefone, nome, id, tipo) => {
  if (!telefone) return;
  
  // Remover caracteres não numéricos
  const phone = telefone.replace(/\D/g, '');
  
  // Verificar se é um número válido
  if (phone.length < 10) {
    showNotification('Número de telefone inválido', 'error');
    return;
  }
  
  // Construir mensagem com base no tipo de destinatário
  let message = '';
  
  if (tipo === 'cliente') {
    message = `Olá ${nome}, sobre sua assistência técnica ${id} no Preço do Gás...`;
  } else if (tipo === 'revenda') {
    message = `Olá ${nome}, sobre a assistência técnica ${id} no Preço do Gás...`;
  }
  
  // Codificar mensagem para URL
  const encodedMessage = encodeURIComponent(message);
  
  // Abrir WhatsApp
  window.open(`https://wa.me/55${phone}?text=${encodedMessage}`, '_blank');
};

// Função para abrir modal de observação
const openObservationModal = (assistencia, type = 'assistencia') => {
  if (!assistencia) return;

  let text = '';
  let modalTitle = '';

  if (type === 'assistencia') {
    text = assistencia.observacao || '';
    modalTitle = 'Observação da Assistência';
  } else if (type === 'revenda') {
    text = assistencia.observacaorevenda || '';
    modalTitle = 'Observação da Revenda';
  }

  observationData.value = {
    assistencia,
    text,
    type,
    title: modalTitle
  };
  
  observationModalOpen.value = true;
};

// Função para salvar observação
const salvarObservacao = async () => {
  const { assistencia, text, type } = observationData.value;
  if (!assistencia) return;
  
  try {
    let response;

    if (type === 'assistencia') {
      response = await assistenciaStore.atualizarObservacaoAssistencia(assistencia.id, text);
      if (response.success) {
        assistencia.observacao = text;
      }
    } else if (type === 'revenda') {
      response = await assistenciaStore.atualizarObservacaoRevendaAssistencia(assistencia.id, text);
      if (response.success) {
        assistencia.observacaorevenda = text;
      }
    }
    
    if (response && response.success) {
      showNotification(`Observação ${type === 'revenda' ? 'da revenda' : 'da assistência'} atualizada com sucesso!`, 'success');
      observationModalOpen.value = false;
    } else {
      showNotification(`Erro ao atualizar observação ${type === 'revenda' ? 'da revenda' : 'da assistência'}`, 'error');
    }
  } catch (error) {
    console.error('Erro ao salvar observação:', error);
    showNotification('Erro ao atualizar observação', 'error');
  }
};

// Função para fechar modal de observação
const closeObservationModal = () => {
  observationModalOpen.value = false;
  observationData.value = { assistencia: null, text: '' };
};

// Rejeitar assistência
const handleRejectAssistencia = async (assistencia, motivo) => {
  try {
    rejectionLoading.value = true;
    
    // Prepare payload with idrevenda and motivo
    const payload = {
      idrevenda: assistencia.revenda.id,
      motivo: motivo
    };
    
    const result = await assistenciaStore.rejeitarAssistencia(assistencia.id, payload);
    
    if (result.success) {
      showNotification('Assistência rejeitada com sucesso!', 'success');
      rejectionModalOpen.value = false;
      return true;
    } else {
      showNotification(result.message || 'Falha ao rejeitar assistência', 'error');
      return false;
    }
  } catch (error) {
    console.error('Erro ao rejeitar assistência:', error);
    showNotification('Falha ao rejeitar assistência', 'error');
    return false;
  } finally {
    rejectionLoading.value = false;
  }
};

// Set up confirmation capability
const { 
  confirmationOpen,
  confirmationConfig,
  confirm, 
  handleConfirm, 
  handleCancel,
  setLoading
} = useConfirmation();

// Set up a more robust way to check if any modal is open
onMounted(() => {
  // Set up the global modal tracking function
  if (!window.isAnyAssistenciaModalOpen) {
    window.isAnyAssistenciaModalOpen = () => {
      return confirmationOpen.value || 
             rejectionModalOpen.value || 
             observationModalOpen.value ||
             editModalOpen.value;
    };
  }
});

// Helper function to determine the current status description
const getStatusDescription = computed(() => {
  const assistencia = props.assistencia;
  
  switch (true) {
    case (assistencia.dtentrega !== null && assistencia.status == 3): // Assistência Concluída
      return {
        text: 'Concluída em '+assistenciaStore.formatDate(assistencia.dtentrega),
        icon: 'mdi-check-circle',
        iconColor: 'success',
        data: assistencia.dtentrega,
        label: 'Data de Conclusão:'
      };
    case (assistencia.dtcancel !== null && assistencia.status == 4): // Assistência Cancelada
      return {
        text: 'Cancelada em '+assistenciaStore.formatDate(assistencia.dtcancel),
        icon: 'mdi-close-circle',
        iconColor: 'error',
        label: 'Data de Cancelamento:',
        reason: assistencia.motivocancel
      };
    case (assistencia.dtsaida !== null && assistencia.status == 2): // Saiu para atendimento
      return {
        text: 'Saiu em '+assistenciaStore.formatDate(assistencia.dtsaida),
        icon: 'mdi-truck-delivery',
        iconColor: 'info',
        label: 'Data de Saída:'
      };
    case (assistencia.dtaceite !== null && assistencia.status == 2): // Assistência Aceita
      return {
        text: 'Aceita em '+assistenciaStore.formatDate(assistencia.dtaceite),
        icon: 'mdi-check',
        iconColor: 'success',
        label: 'Data de Aceitação:'
      };
    default:
      return {
        text: 'Aberta em '+assistenciaStore.formatDate(assistencia.dtsolicitacao),
        icon: 'mdi-clock-outline',
        iconColor: 'warning',
        label: 'Data de Solicitação:'
      };
  }
});
</script>

<template>
  <v-card class="mb-3 order-card" variant="outlined">
    <!-- Card header com tabs -->
    <v-card-title class="text-subtitle-1 pb-1">
      <v-tabs
        v-model="assistenciaStore.activeTab[assistencia.id]"
        align-tabs="start"
        color="primary"
        density="compact"
        class="compact-tabs"
      >
        <v-tab class="compact-tab" value="tabAssistencia">Dados</v-tab>
        <v-tab class="compact-tab" value="tabCliente">Cliente</v-tab>
        <v-tab class="compact-tab" value="tabRevenda">Revenda</v-tab>
        <v-tab class="compact-tab" value="tabAcoes">Ações</v-tab>
      </v-tabs>
    </v-card-title>
    <v-divider></v-divider>
    
    <!-- Card content -->
    <v-card-text class="pt-2">
      <!-- Assistência tab -->
      <div v-if="assistenciaStore.activeTab[assistencia.id] === 'tabAssistencia'">
        <div class="d-flex justify-space-between">
          <span>
            <strong>#{{ assistencia.id }}</strong>
            <v-icon class="copy-icon" size="x-small" color="primary" @click="copyToClipboard(assistencia.id, 'Número da assistência')">mdi-content-copy</v-icon>
          </span>
          <span title="Data de previsão">{{ assistenciaStore.formatDate(assistencia.dtprevisao) }}</span>
        </div>
        
        <!-- Add status description with date data -->
        <div class="mb-1">
          <v-icon 
            size="small" 
            :color="getStatusDescription.iconColor" 
            class="mr-1"
          >{{ getStatusDescription.icon }}</v-icon>
          <span>{{ getStatusDescription.text }}</span>
          <span v-if="getStatusDescription.reason"><br><strong>Motivo:</strong> {{ getStatusDescription.reason }}</span>
        </div>
        
        <!-- Date info based on status -->
        <div class="mb-1">
          <strong>Data de Solicitação</strong> {{ assistenciaStore.formatDate(assistencia.dtsolicitacao) }}
        </div>
        
        <div v-if="assistencia.pedido_id" class="mb-1">
          <strong>Pedido Original:</strong> #{{ assistencia.pedido_id }}
          <v-icon class="copy-icon" size="x-small" color="primary" @click="copyToClipboard(assistencia.pedido_id, 'Número do pedido original')">mdi-content-copy</v-icon>
        </div>
        
        <div class="mb-1">
          <strong>Origem:</strong> {{ assistencia.origem }}
        </div>
        
        <div v-if="assistencia.produto">
          <strong>Produto:</strong> {{ assistencia.produto.tipoproduto.tipo }} - {{ assistencia.produto.fabricante.nome }}
        </div>
        
        <div v-if="assistencia.descricao" class="mt-0 assistance-description">
          <div class="d-flex align-center justify-space-between">
            <div>
              <strong>Solicitação:</strong>
              <p>{{ assistencia.descricao }}</p>
            </div>
          </div>
        </div>

        <div v-if="assistencia.observacao" class="mt-2 assistance-description">
          <div class="d-flex align-center justify-space-between">
            <div><strong>Obs: </strong>{{ assistencia.observacao }}</div>
            <v-icon class="edit-icon" size="small" color="grey-darken-1" @click="openObservationModal(assistencia, 'assistencia')">mdi-pencil</v-icon>
          </div>
        </div>

        <div v-if="assistencia.observacaorevenda" class="mt-2 assistance-description obsrevenda">
          <div class="d-flex align-center justify-space-between">
            <div><strong>Obs revenda: </strong>{{ assistencia.observacaorevenda }}</div>
            <v-icon class="edit-icon" size="small" color="grey-darken-1" @click="openObservationModal(assistencia, 'revenda')">mdi-pencil</v-icon>
          </div>
        </div>

        <!-- Add the button for assistencias with status = 1 -->
        <v-divider v-if="assistencia.status === '1'" class="my-2"></v-divider>
        
        <v-btn 
          v-if="assistencia.status === '1'" 
          block 
          size="small" 
          style="background-color: #d43ab1;"
          prepend-icon="mdi-bell-outline"
          @click="handleAvisarAssistencia(assistencia)"
          :loading="avisandoAssistencia"
          :disabled="avisandoAssistencia"
        >
          Avisar assistência
        </v-btn>
      </div>
      
      <!-- Cliente tab -->
      <div v-else-if="assistenciaStore.activeTab[assistencia.id] === 'tabCliente'">
        <div v-if="assistencia.cliente.nome === ''" class="text-center py-4">
          <v-icon color="warning" size="large" class="mb-2">mdi-account-off</v-icon>
          <div class="text-h6">Cliente Excluído</div>
        </div>
        
        <div v-else>
          <div class="mb-1">
            <strong>Nome:</strong> {{ assistencia.cliente.nome }}
          </div>
          
          <div class="mb-1">
            <strong>Telefone:</strong> {{ assistencia.cliente.telefone1 }}
            <v-icon title="Copiar telefone do cliente" class="copy-icon" size="x-small" color="primary" @click="copyToClipboard(assistencia.cliente.telefone1, 'Telefone do cliente')">mdi-content-copy</v-icon>
            <v-icon 
              class="action-icon whatsapp-icon" 
              size="small" 
              color="green" 
              title="Notificar via WhatsApp"
              @click="openWhatsApp(assistencia.cliente.telefone1, assistencia.cliente.nome, assistencia.id, 'cliente')"
            >mdi-whatsapp</v-icon>
          </div>
        </div>

        <div class="mb-1">
          <strong>Endereço:</strong> {{ assistencia.endereco_entrega }}
          <v-icon title="Copiar endereço" class="copy-icon" size="x-small" color="primary" @click="copyToClipboard(assistencia.endereco_entrega, 'Endereço de entrega')">mdi-content-copy</v-icon>
        </div>
        
        <div v-if="assistencia.cliente.nome !== ''">
          <v-divider class="my-2"></v-divider>
          <v-btn 
            color="indigo" 
            block 
            size="small" 
            prepend-icon="mdi-map-marker"
            @click="handleNotificarEstouNaPorta(assistencia)"
            :loading="loadNotificarEstouNaPorta"
            :disabled="loadNotificarEstouNaPorta">
            Notificar "Estou na porta"
          </v-btn>
        </div>
      </div>

    <!-- Revenda tab -->
      <div v-else-if="assistenciaStore.activeTab[assistencia.id] === 'tabRevenda'">
        <div class="mb-1">
          <strong>Revenda:</strong> {{ assistencia.revenda.nomefantasia }}
        </div>
        <div style="margin-bottom: -5px;">
          <strong>Tempo Médio de Entrega:</strong> {{ assistencia.revenda.tempo_medio_calculo > 0 ? "~"+Math.floor(assistencia.revenda.tempo_medio_calculo)+" min" : "-"}}
        </div>

        
        <v-divider class="my-2"></v-divider>
        <div class="mb-1"><strong>Telefones:</strong></div>
        <div class="mb-1" v-if="assistencia.revenda.revendedor?.telefone1">
          Fixo: {{ assistencia.revenda.revendedor.telefone1 }}
          <v-icon class="copy-icon" size="x-small" color="primary" @click="copyToClipboard(assistencia.revenda.revendedor.telefone1, 'Telefone fixo')">mdi-content-copy</v-icon>
        </div>
        <div class="mb-1" v-if="assistencia.revenda.revendedor?.telefone2">
          Fixo 2: {{ assistencia.revenda.revendedor.telefone2 }}
          <v-icon class="copy-icon" size="x-small" color="primary" @click="copyToClipboard(assistencia.revenda.revendedor.telefone2, 'Telefone fixo 2')">mdi-content-copy</v-icon>
        </div>
        <div class="mb-1" v-if="assistencia.revenda.revendedor?.celular">
          Celular: {{ assistencia.revenda.revendedor.celular }}
          <v-icon class="copy-icon" size="x-small" color="primary" @click="copyToClipboard(assistencia.revenda.revendedor.celular, 'Celular')">mdi-content-copy</v-icon>
          <v-icon 
            class="action-icon whatsapp-icon" 
            size="small" 
            color="green" 
            title="Notificar via WhatsApp"
            @click="openWhatsApp(assistencia.revenda.revendedor.celular, assistencia.revenda.nomefantasia, assistencia.id, 'revenda')"
          >mdi-whatsapp</v-icon>
        </div>
        <div class="mb-1" v-if="assistencia.revenda.revendedor?.celularresponsavel">
          Responsável: {{ assistencia.revenda.revendedor.celularresponsavel }}
          <v-icon class="copy-icon" size="x-small" color="primary" @click="copyToClipboard(assistencia.revenda.revendedor.celularresponsavel, 'Celular responsável')">mdi-content-copy</v-icon>
          <v-icon 
            class="action-icon whatsapp-icon" 
            size="small" 
            color="green" 
            title="Notificar via WhatsApp"
            @click="openWhatsApp(assistencia.revenda.revendedor.celularresponsavel, assistencia.revenda.nomefantasia, assistencia.id, 'revenda')"
          >mdi-whatsapp</v-icon>
        </div>
        
        <v-divider class="my-2"></v-divider>
        <div class="mb-1"><strong>Horários de funcionamento:</strong></div>
        <div v-if="assistencia.revenda.horarios_funcionamento && assistencia.revenda.horarios_funcionamento.length > 0">
          <div v-for="(horario, index) in assistencia.revenda.horarios_funcionamento" :key="index" class="mb-1">
            {{ horario.descricao }}: {{ horario.horainicio.substring(0, 5) }} - {{ horario.horafim.substring(0, 5) }}
          </div>
        </div>
        <div v-else class="text-body-2 text-grey">Sem horários cadastrados</div>
        
      </div>
      
      <!-- Ações tab - Aqui é onde adicionamos o seletor de alteração de status -->
      <div v-else-if="assistenciaStore.activeTab[assistencia.id] === 'tabAcoes'">
        <!-- Status change selector - adicionar no topo -->
        <v-select
          density="compact"
          variant="outlined"
          :items="[
            { title: 'Aceitar', value: '2' },
            { title: 'Rejeitar', value: '4' },
            { title: 'Saiu para Assistência', value: '8' },
            { title: 'Concluída', value: '3' },
          ]"
          item-title="title"
          item-value="value"
          v-model="selectedStatus"
          @update:model-value="onStatusChange"
          :loading="selectedStatus && isOperationLoading(assistencia.id, `status-${selectedStatus}`)"
          :disabled="selectedStatus && isOperationLoading(assistencia.id, `status-${selectedStatus}`)"
          hide-details
          class="mb-2"
        >
          <template v-slot:prepend-inner>
            <v-icon size="small" color="primary">mdi-refresh</v-icon>
          </template>
          <template v-slot:label>
            <span style="color: #000000;">Alterar status</span>
          </template>
        </v-select>
        
        <!-- Conteúdo existente da aba Ações -->
        <v-btn 
          color="info" 
          block 
          class="mb-2" 
          size="small" 
          prepend-icon="mdi-pencil"
          @click="openEditModal"
        >
          Editar informações
        </v-btn>

        <v-btn 
          v-if="assistencia.observacao === null || assistencia.observacao === ''" 
          color="amber-darken-1" 
          block 
          class="mb-2"
          size="small" 
          prepend-icon="mdi-pencil"
          @click="openObservationModal(assistencia, 'assistencia')"
        >
          Observação da assistência
        </v-btn>

        <v-btn 
          v-if="assistencia.observacaorevenda === null || assistencia.observacaorevenda === ''" 
          color="deep-purple" 
          block 
          size="small" 
          prepend-icon="mdi-pencil"
          @click="openObservationModal(assistencia, 'revenda')"
        >
          Observação da revenda
        </v-btn>
      </div>
    </v-card-text>
  </v-card>

  <!-- Modal de edição da assistência técnica -->
  <EditarAssistenciaTecnicaModal
    :open="editModalOpen"
    @update:open="editModalOpen = $event"
    :assistencia="assistencia"
    :loading="editModalLoading"
    @save="saveEditedAssistencia"
    @cancel="cancelEditModal"
  />

  <!-- Modal de observação -->
  <v-dialog v-model="observationModalOpen" max-width="600" persistent>
    <v-card>
      <v-card-title class="text-h5 bg-primary text-white">
        <v-icon start icon="mdi-note-text" class="mr-2"></v-icon>
        {{ observationData.title || 'Editar Observação' }}
      </v-card-title>
      
      <v-card-text class="pt-4">
        <v-form @submit.prevent="salvarObservacao">
          <v-textarea
            v-model="observationData.text"
            :label="observationData.type === 'revenda' ? 'Observação da Revenda' : 'Observação da Assistência'"
            rows="5"
            auto-grow
            variant="outlined"
            hide-details="auto"
            class="mb-3"
          ></v-textarea>
          
          <div class="d-flex justify-end mt-4">
            <v-btn color="grey-darken-1" variant="text" class="mr-2" @click="closeObservationModal">
              Cancelar
            </v-btn>
            <v-btn color="primary" type="submit">
              Salvar
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
  
  <!-- Confirmation modal -->
  <ConfirmationModal
    v-if="confirmationOpen"
    v-model:open="confirmationOpen"
    :title="confirmationConfig.title"
    :message="confirmationConfig.message"
    :confirm-text="confirmationConfig.confirmText"
    :cancel-text="confirmationConfig.cancelText"
    :confirm-color="confirmationConfig.confirmColor"
    :loading="confirmationConfig.loading"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  />

  <!-- Modal para rejeitar assistência -->
  <RejeitarAssistenciaModal
    v-model:open="rejectionModalOpen"
    :assistencia="assistencia"
    :loading="rejectionLoading"
    @rejeitar="handleRejectAssistencia"
    @cancelar="rejectionModalOpen = false"
  />
</template>

<style scoped>
/* Order card styles */
.order-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.order-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

/* Compact tabs */
.compact-tabs :deep(.v-tab) {
  min-width: 0 !important;
  padding: 0 8px !important;
  font-size: 0.9rem !important;
  justify-content: center;
}

.compact-tabs :deep(.v-tabs-bar) {
  height: 32px !important;
}

.compact-tab {
  text-transform: none !important;
  letter-spacing: normal !important;
}

/* Icons */
.copy-icon {
  margin-left: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.2s, transform 0.2s;
}

.copy-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}

.action-icon {
  margin-left: 6px;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s, transform 0.2s;
}

.action-icon:hover {
  opacity: 1;
  transform: scale(1.2);
}

.assistance-description {
  background-color: rgba(0,0,0,0.1);
  border-radius: 4px;
  padding: 8px;
  transition: background-color 0.2s;
}

.assistance-description.obsrevenda{
  background-color: rgba(244, 67, 54, 0.3);
  color: #f44336;
}

.assistance-description:hover {
  background-color: rgba(0,0,0,0.15);
}
.assistance-description.obsrevenda:hover {
  background-color: rgba(244, 67, 54, 0.4);
}
</style>
